import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';

import { type WizardComponent } from '@/components/shared/Wizard/components';

import { findNextEnabledStep, findPreviousEnabledStep, normalizeSteps } from './functions';

type StepListener = (data: { step: string }) => void;

export type Step = {
  content: WizardComponent;
  /** @default true */
  enabled?: boolean;
  /** @default true */
  scroll?: boolean;
  /** @default false */
  merge?: boolean;
  /** Timeout until next step (milliseconds) */
  timeout?: number;
  /** @default false */
  fullHeight?: boolean;
  /** Delay after next step is triggered (milliseconds) */
  delay?: number;
  /** Progress value from 0 to 100 */
  progress?: number;
  /** @default true */
  returnable?: boolean;
  /** @default 1 */
  skipOnReturn?: number;
  /** @default 1 (inserted by addSteps) */
  stepsToRemoveOnReturn?: number;
  /** @default false */
  hidePreviewContainer?: boolean;
  /** @default false */
  hidePreview?: boolean;
  data?: any;
};

export type StepsStore = {
  steps: Step[];
  contentHistory: {
    content: Step['content'];
    renderedAt: string;
    origin: 'setSteps' | 'next' | 'previous' | 'setStep';
  }[];
  stepChangedListeners: StepListener[];
  step: number;
  stepData?: Step;
  isLocked?: boolean;
  setLocked: (value?: boolean) => void;
  clean: () => void;
  setSteps: (steps: Step[]) => void;
  addListener: (listener: StepListener) => void;
  removeListener: (listener: StepListener) => void;
  triggerStepEvent: (step: string) => void;
  triggerFinished: () => void;
  addSteps: (options: { steps: Step[]; removeStepsOnReturn?: boolean }) => void;
  removeStepsAhead: (content?: Step['content']) => void;
  nextStep: (options?: { validate?: () => boolean; skip?: number }) => void;
  previousStep: (options?: { onStepNotFound?: () => void }) => void;
  setStep: (step: number) => void;
};

export const stepStore = create<StepsStore>()(
  subscribeWithSelector((set, get) => ({
    steps: [],
    contentHistory: [],
    stepChangedListeners: [] as StepListener[],
    step: 0,
    currentStep: undefined,
    setLocked: (value?: boolean) => {
      set({ isLocked: value });
    },
    addListener: (listener: StepListener) => {
      set(state => ({
        stepChangedListeners: [...state.stepChangedListeners, listener]
      }));
    },
    triggerStepEvent: (step: string) => {
      const { stepChangedListeners } = get();
      stepChangedListeners.forEach(listener => listener({ step }));
    },
    triggerFinished: () => {
      const { triggerStepEvent } = get();
      triggerStepEvent('finished');
    },
    removeListener: (listener: StepListener) => {
      set(state => ({
        stepChangedListeners: state.stepChangedListeners.filter(l => l !== listener)
      }));
    },
    clean: () => {
      set({
        steps: [],
        contentHistory: [],
        step: 0,
        stepData: undefined,
        isLocked: false
      });
    },
    setSteps: (steps: Step[]) => {
      const normalizedSteps = normalizeSteps(steps);
      set({
        steps: normalizedSteps,
        step: 0,
        stepData: normalizedSteps[0],
        contentHistory: [
          {
            content: normalizedSteps[0]?.content,
            renderedAt: new Date().toISOString(),
            origin: 'setSteps'
          }
        ]
      });
    },
    addStepsAtEnd: (steps: Step[]) => {
      const { steps: currentSteps } = get();
      const normalizedSteps = normalizeSteps(steps);
      set({ steps: [...currentSteps, ...normalizedSteps] });
    },
    addSteps: options => {
      const { steps: currentSteps, step } = get();
      const normalizedSteps = normalizeSteps(options.steps);

      const pastSteps = currentSteps.slice(0, step + 1);

      if (options.removeStepsOnReturn) {
        pastSteps[pastSteps.length - 1] = {
          ...pastSteps[pastSteps.length - 1],
          stepsToRemoveOnReturn: normalizedSteps.length
        };
      }

      const newSteps = [...pastSteps, ...normalizedSteps, ...currentSteps.slice(step + 1)];

      set({ steps: newSteps });
    },
    removeStepsAhead: (content?: Step['content']) => {
      const { steps, step } = get();

      const findStepIndex = () => {
        if (content) {
          return steps.findIndex(s => s.content === content);
        }

        return step;
      };

      const currentStepIndex = findStepIndex();

      if (!currentStepIndex || !steps[currentStepIndex]?.stepsToRemoveOnReturn) {
        return;
      }

      const removalEndIndex = currentStepIndex + 1 + steps[currentStepIndex].stepsToRemoveOnReturn;
      const newSteps = [...steps.slice(0, currentStepIndex + 1), ...steps.slice(removalEndIndex)];
      set({ steps: newSteps });
    },
    nextStep: params => {
      const { step, steps, stepData, contentHistory, triggerStepEvent } = get();

      if (!Boolean(steps.length)) {
        return;
      }

      if (params?.validate && !params.validate()) {
        return;
      }

      const next = findNextEnabledStep(step, steps, params?.skip);

      if (next === undefined) {
        return;
      }

      const nextData = steps[next];

      if (!nextData) {
        return;
      }

      const updatedContentHistory = [
        ...contentHistory,
        {
          content: nextData.content,
          renderedAt: new Date().toISOString(),
          origin: 'next' as StepsStore['contentHistory'][number]['origin']
        }
      ];

      const setData = {
        step: next,
        stepData: nextData,
        contentHistory: updatedContentHistory
      };

      setTimeout(() => {
        set(setData);

        if (stepData?.merge) {
          triggerStepEvent(stepData?.content ?? 'unknown');
        }
      }, stepData?.delay || 0);
    },
    previousStep: params => {
      const { step, steps, stepData, contentHistory } = get();

      if (!Boolean(steps.length)) {
        return;
      }

      const previous = findPreviousEnabledStep(step, steps, stepData?.skipOnReturn);

      if (previous === undefined) {
        params?.onStepNotFound?.();
        return;
      }

      const previousData = steps[previous];
      const updatedContentHistory = [
        ...contentHistory,
        {
          content: previousData.content,
          renderedAt: new Date().toISOString(),
          origin: 'previous' as StepsStore['contentHistory'][number]['origin']
        }
      ];

      set({
        step: previous,
        stepData: previousData,
        contentHistory: updatedContentHistory
      });
    },
    setStep: (step: number) => {
      const { steps, contentHistory } = get();

      if (!Boolean(steps.length)) {
        return;
      }

      const nextData = steps[step];

      if (!nextData) {
        return;
      }

      if (!nextData.enabled) {
        return;
      }

      const updatedContentHistory = [
        ...contentHistory,
        {
          content: nextData.content,
          renderedAt: new Date().toISOString(),
          origin: 'setStep' as StepsStore['contentHistory'][number]['origin']
        }
      ];
      set({
        step,
        stepData: nextData,
        contentHistory: updatedContentHistory
      });
    }
  }))
);

const useStepStore = <T>(selector: (state: StepsStore) => T): T => {
  return stepStore(useShallow(selector));
};

export default useStepStore;
